'use strict';

const libPictView = require('pict-view');

/**
 * AssetCatalogSync — the #/AssetCatalogSync screen. A single-pane action surface that seeds THIS customer's
 * PhysicalAsset* catalog (element types, materials, defects, and their links) from the shared public data
 * lake (the AASHTO/NBE national standard). All logic + data live in AssetCatalogSyncProvider, which paints the
 * plan table, buttons, and result into #AssetCatalogSync-Body. See that provider for the mapping + safety
 * (additive create-missing; never overwrites a customer's tuned rows).
 */
class AssetCatalogSync extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.AssetCatalogSync; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			if (!tmpProvider.state.Loaded && !tmpProvider.state.Loading) { tmpProvider.loadPlanAsync(); }
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = AssetCatalogSync;
module.exports.default_configuration =
{
	ViewIdentifier: 'AssetCatalogSync',
	DefaultRenderable: 'AssetCatalogSyncWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.acs-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 60rem; }
		.acs-head { margin: 0 0 1rem; }
		.acs-title { font-size:1.5rem; font-weight:700; line-height:1.2; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.acs-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.25rem 0 0; max-width:44rem; }
		.acs-panel { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.1rem 1.25rem; }
		.acs-note { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); padding:0.5rem 0; }
		.acs-note-inline { font-size:0.82rem; color:var(--theme-color-text-muted,#8894a4); margin-left:0.6rem; }
		.acs-error { font-size:0.9rem; color:var(--theme-color-status-error,#b3382f); background:var(--theme-color-status-error-bg,#fbeaea); border-radius:6px; padding:0.55rem 0.75rem; margin:0 0 0.9rem; }
		.acs-summary { font-size:0.92rem; color:var(--theme-color-text-primary,#1f2733); margin:0 0 0.9rem; }
		.acs-summary-ok { color:var(--theme-color-text-muted,#6b7686); }
		.acs-table { width:100%; border-collapse:collapse; margin:0 0 1rem; font-size:0.9rem; }
		.acs-table th { text-align:left; font-size:0.7rem; font-weight:800; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-secondary,#45505f); border-bottom:1px solid var(--theme-color-border-light,#eef1f6); padding:0.4rem 0.5rem; }
		.acs-table td { border-bottom:1px solid var(--theme-color-border-light,#f1f4f8); padding:0.5rem; color:var(--theme-color-text-primary,#1f2733); }
		.acs-table .acs-num { text-align:right; font-variant-numeric: tabular-nums; }
		.acs-cell-label i { color:var(--theme-color-text-muted,#8894a4); width:1.1rem; text-align:center; margin-right:0.35rem; }
		.acs-badge { display:inline-block; font-size:0.72rem; font-weight:700; border-radius:999px; padding:0.12rem 0.55rem; }
		.acs-badge-new { color:#1b6e3a; background:#e4f5ea; }
		.acs-badge-ok { color:#5a6472; background:#eef1f6; }
		.acs-badge-info { color:#1c5a8a; background:#e6f0f9; }
		.acs-result { font-size:0.9rem; color:#1b6e3a; background:#e4f5ea; border-radius:6px; padding:0.55rem 0.75rem; margin:0 0 0.9rem; }
		.acs-result i, .acs-badge-new { }
		.acs-actions { display:flex; align-items:center; gap:0.6rem; flex-wrap:wrap; margin-top:0.6rem; }
		.acs-confirm { background:var(--theme-color-status-warning-bg,#fbf3d9); border-radius:7px; padding:0.55rem 0.7rem; }
		.acs-confirm-q { font-size:0.88rem; font-weight:600; color:var(--theme-color-text-primary,#1f2733); }
		.acs-progress { font-size:0.88rem; color:var(--theme-color-text-muted,#6b7686); }
		.acs-btn { font-size:0.85rem; font-weight:600; padding:0.45rem 0.85rem; border-radius:7px; border:1px solid transparent; cursor:pointer; }
		.acs-btn[disabled] { opacity:0.55; cursor:default; }
		.acs-btn-primary { background:var(--theme-color-brand-primary,#2e7d74); color:#fff; }
		.acs-btn-ghost { background:transparent; border-color:var(--theme-color-border-medium,#c7cfda); color:var(--theme-color-text-primary,#1f2733); }
		.acs-btn-ghost:hover { background:var(--theme-color-background-secondary,#eef1f6); }
	`,
	Templates:
	[
		{
			Hash: 'AssetCatalogSync-Wrap',
			Template: /*html*/`<div class="acs-wrap">
				<div class="acs-head">
					<h1 class="acs-title"><i class="fas fa-cubes"></i> Sync AASHTO Catalog</h1>
					<p class="acs-subtitle">Seed this customer's asset catalog — element types, materials, and defects — from the shared public data lake (the AASHTO/NBE national standard). Adds only what's missing; rows you've already tuned are left untouched.</p>
				</div>
				<div class="acs-panel"><div id="AssetCatalogSync-Body"></div></div>
			</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'AssetCatalogSyncWrap',
			TemplateHash: 'AssetCatalogSync-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
